import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { customersApi, vehiclesApi, workOrdersApi, ApiError } from "../lib/api";
import { applyDigitPreservingTransform } from "../lib/inputCursor";
import { useShopType } from "../lib/theme";
import { Eyebrow, ModernBreadcrumbs, ModernButton, ModernCard, ModernErrorBanner, ModernField, ModernInput } from "../components/ui-modern";

/**
 * The modern theme's New Work Order screen — see docs/FRONTEND_BACKLOG.md
 * "Round 62" (pages outside the original 11 mockup screens; this is the
 * actual fix for the owner's "New Work Order opens classic page" report,
 * since `NewWorkOrderPage.tsx` is what "+ New Work Order" everywhere in the
 * app navigates to). No dedicated mockup screen exists for this multi-step
 * flow; restyled using the established modern vocabulary (ModernCard/
 * ModernField/ModernBreadcrumbs/ModernButton) from WorkOrderEditorPageModern
 * and CustomerDetailPageModern — the two screens this flow hands off
 * to/from.
 *
 * Same data-fetching/mutation/step-state as NewWorkOrderPageClassic.tsx —
 * identical query keys (`["customers","picker",...]`, `["vehicles","picker",
 * customerId]`, `["customer",customerId]`, `["vehicle",vehicleId]`),
 * identical `handleSubmit`/`workOrdersApi.create` payload, identical
 * `?customerId=`/`?vehicleId=` preset handling and breadcrumb logic
 * (including the "arrived via Customer page's Start Work Order" vs. "arrived
 * bare" distinction and the `?returnTo=work-order` round-trip to
 * NewCustomerPage/NewVehiclePage), duplicated here as a thin re-run of
 * Classic's own calls rather than a shared hook, matching every other
 * screen's own tradeoff.
 *
 * No "job" text appears anywhere in Classic's copy for this page (checked
 * explicitly per the owner's 2026-09-13 "Work Order" terminology call) —
 * nothing to rename here.
 */
export function NewWorkOrderPageModern() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  // A detailing-shopType tenant has no vehicles at all (see
  // docs/SHOP_TYPES_PLAN.md) — the "Which Vehicle?" step is skipped
  // entirely and vehicleId is left unset, letting
  // WorkOrdersService.create() supply a hidden placeholder.
  const hasVehicles = useShopType() !== "detailing";
  const presetCustomerId = params.get("customerId") ? Number(params.get("customerId")) : null;
  const presetVehicleId = params.get("vehicleId") ? Number(params.get("vehicleId")) : null;

  const [customerId, setCustomerId] = useState<number | null>(presetCustomerId);
  const [vehicleId, setVehicleId] = useState<number | null>(presetVehicleId);
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerConcern, setCustomerConcern] = useState("");
  // null = untouched, so the field shows the vehicle's last recorded odometer
  // until the user types (including clearing it to empty).
  const [odometerEdit, setOdometerEdit] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const customerResults = useQuery({
    queryKey: ["customers", "picker", customerSearch],
    queryFn: () => customersApi.list({ search: customerSearch, limit: 10 }),
    enabled: !customerId && customerSearch.trim().length > 0,
  });

  const vehicleResults = useQuery({
    queryKey: ["vehicles", "picker", customerId],
    queryFn: () => vehiclesApi.list({ customerId: customerId!, limit: 25 }),
    enabled: hasVehicles && !!customerId && !vehicleId,
  });

  // Once picked (whether typed through the search step above, or arriving
  // preset via ?customerId=&vehicleId= from the Customer page's "Start Work
  // Order" button — which skips straight past both picker steps), the
  // customer/vehicle context card below needs the full records, not just
  // the id the picker/URL handed over.
  const customerQuery = useQuery({
    queryKey: ["customer", customerId],
    queryFn: () => customersApi.get(customerId!),
    enabled: !!customerId,
  });
  const vehicleQuery = useQuery({
    queryKey: ["vehicle", vehicleId],
    queryFn: () => vehiclesApi.get(vehicleId!),
    enabled: !!vehicleId,
  });

  const lastOdometer = vehicleQuery.data?.vehicle.lastOdometer ?? null;
  const odometerIn = odometerEdit ?? (lastOdometer != null ? String(lastOdometer) : "");

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!customerId || (hasVehicles && !vehicleId)) return;
    if (!customerConcern.trim()) {
      setError("Please describe the problem/concern.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const { aggregate } = await workOrdersApi.create({
        customerId,
        vehicleId: vehicleId ?? undefined,
        customerConcern: customerConcern.trim(),
        odometerIn: odometerIn ? Number(odometerIn) : null,
      });
      navigate(`/work-orders/${aggregate.workOrder.workOrderNumber}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not start this work order. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  // Reflects how this page was actually reached: arriving with a preset
  // customerId (the Customer page's "Start Work Order" button on a
  // vehicle card) means "Customers" is the real parent; arriving bare
  // (Work Orders list / Dashboard's own "+ New Work Order") means "Work
  // Orders" is. Either way, once the customer is known (preset, or picked
  // via the search step below), their name is inserted as its own crumb
  // linking back to their page — matching NewVehiclePage/NewCustomerPage's
  // own "New Work Order" round-trip crumb convention.
  const breadcrumbItems = [
    presetCustomerId ? { label: "Customers", to: "/customers" } : { label: "Work Orders", to: "/work-orders" },
    ...(customerQuery.data
      ? [{ label: customerQuery.data.customer.displayName, to: `/customers/${customerQuery.data.customer.customerNumber}` }]
      : []),
    { label: "New Work Order" },
  ];

  return (
    <div className="mx-auto max-w-xl">
      <ModernBreadcrumbs items={breadcrumbItems} />
      <Eyebrow className="mb-2 text-[11px] text-[#0f6d86]">New</Eyebrow>
      <h1 className="m-0 mb-4 text-[28px] font-bold text-[#071a33]">New Work Order</h1>

      {/* Same "Customer"/"Vehicle" context-card look as WorkOrderEditorPageModern
          (the screen this flow lands on next) — stacked rather than
          side-by-side since this page's container is much narrower.
          Appears as soon as each is picked, not just once both are, so the
          customer stays visible while still choosing a vehicle. */}
      {customerQuery.data && (
        <ModernCard className="mb-3">
          <Eyebrow className="mb-1.5 text-[11px] text-[#6b7a8f]">Customer</Eyebrow>
          <div className="text-[16px] font-semibold text-[#071a33]">{customerQuery.data.customer.displayName}</div>
          {customerQuery.data.customer.phones[0] && (
            <div className="mt-0.5 text-[13.5px] text-[#6b7a8f]">{customerQuery.data.customer.phones[0].phoneNumber}</div>
          )}
          {customerQuery.data.customer.email && (
            <div className="mt-0.5 text-[13.5px] text-[#6b7a8f]">{customerQuery.data.customer.email}</div>
          )}
        </ModernCard>
      )}
      {vehicleQuery.data && (
        <ModernCard className="mb-3">
          <Eyebrow className="mb-1.5 text-[11px] text-[#6b7a8f]">Vehicle</Eyebrow>
          <div className="text-[16px] font-semibold text-[#071a33]">
            {[vehicleQuery.data.vehicle.modelYear, vehicleQuery.data.vehicle.make, vehicleQuery.data.vehicle.model, vehicleQuery.data.vehicle.trim]
              .filter(Boolean)
              .join(" ") || "Vehicle with no details yet"}
          </div>
          {vehicleQuery.data.vehicle.licensePlate && (
            <div className="mt-0.5 text-[13.5px] text-[#6b7a8f]">Plate {vehicleQuery.data.vehicle.licensePlate}</div>
          )}
          {vehicleQuery.data.vehicle.vin && <div className="mt-0.5 text-[13.5px] text-[#6b7a8f]">VIN {vehicleQuery.data.vehicle.vin}</div>}
          {lastOdometer != null && (
            <div className="mt-0.5 text-[13.5px] text-[#6b7a8f]">Last odometer {lastOdometer.toLocaleString()} mi</div>
          )}
        </ModernCard>
      )}

      <ModernCard>
        {error && <ModernErrorBanner message={error} />}

        {!customerId && (
          <ModernField label="Find the Customer" htmlFor="customerSearchModern">
            <ModernInput
              id="customerSearchModern"
              autoFocus
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              placeholder="Start typing…"
            />
            <p className="mt-1.5 text-[12.5px] text-[#8b97a8]">Type a name or phone number.</p>
            <div className="mt-3 flex flex-col gap-2">
              {customerResults.data?.items.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setCustomerId(c.id)}
                  className="rounded-[10px] border border-[#e2e7ee] px-4 py-3 text-left text-[14px] text-[#101b2d] hover:border-[#0f6d86]"
                >
                  {c.displayName} <span className="text-[#6b7a8f]">· Cust #{c.customerNumber}</span>
                </button>
              ))}
              {customerResults.data && customerResults.data.items.length === 0 && (
                <div className="rounded-[10px] border border-dashed border-[#e2e7ee] px-4 py-3">
                  <p className="mb-2.5 text-[13.5px] text-[#6b7a8f]">No match for "{customerSearch}".</p>
                  <Link
                    to={`/customers/new?returnTo=work-order${
                      /[a-z]/i.test(customerSearch) ? `&name=${encodeURIComponent(customerSearch.trim())}` : ""
                    }`}
                  >
                    <ModernButton type="button" variant="secondary">
                      + Add New Customer
                    </ModernButton>
                  </Link>
                </div>
              )}
            </div>
          </ModernField>
        )}

        {hasVehicles && customerId && !vehicleId && (
          <ModernField label="Which Vehicle?" htmlFor="vehiclePickerModern">
            <div className="flex flex-col gap-2">
              {vehicleResults.data?.items.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => setVehicleId(v.id)}
                  className="rounded-[10px] border border-[#e2e7ee] px-4 py-3 text-left text-[14px] text-[#101b2d] hover:border-[#0f6d86]"
                >
                  {[v.modelYear, v.make, v.model, v.trim].filter(Boolean).join(" ") || "Vehicle with no details yet"}
                </button>
              ))}
              {vehicleResults.data && vehicleResults.data.items.length === 0 && (
                <p className="text-[13.5px] text-[#6b7a8f]">This customer has no vehicles yet.</p>
              )}
              {customerQuery.data && (
                <Link to={`/customers/${customerQuery.data.customer.customerNumber}/vehicles/new?returnTo=work-order`}>
                  <ModernButton type="button" variant="secondary" className="w-full">
                    + Add a Vehicle
                  </ModernButton>
                </Link>
              )}
            </div>
          </ModernField>
        )}

        {customerId && (!hasVehicles || vehicleId) && (
          <form onSubmit={(e) => void handleSubmit(e)}>
            <ModernField label="What's the Problem/Concern?" htmlFor="concernModern" required>
              <ModernInput id="concernModern" autoFocus required uppercase value={customerConcern} onChange={(e) => setCustomerConcern(e.target.value)} />
              <p className="mt-1.5 text-[12.5px] text-[#8b97a8]">What the customer told you, in their own words.</p>
            </ModernField>
            {hasVehicles && (
              <ModernField label="Odometer Reading (In)" htmlFor="odometerInModern">
                <ModernInput
                  id="odometerInModern"
                  inputMode="numeric"
                  value={odometerIn}
                  onChange={(e) => setOdometerEdit(applyDigitPreservingTransform(e.target, (v) => v.replace(/\D/g, "")))}
                />
              </ModernField>
            )}
            <div className="flex gap-2.5">
              <ModernButton type="submit" disabled={submitting}>
                {submitting ? "Starting…" : "Start Work Order"}
              </ModernButton>
              <ModernButton type="button" variant="secondary" onClick={() => navigate(-1)}>
                Cancel
              </ModernButton>
            </div>
          </form>
        )}
      </ModernCard>
    </div>
  );
}
